import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { MaskedInput } from '@/components/ui/masked-input';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, MapPin, Building2, Trash2, Settings, History, Cpu } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { DualListBox } from '@/components/ui/dual-listbox';
import { LocationImageUpload } from '../LocationImageUpload';
import { LocationChangelog } from '../LocationChangelog';
import { getLocationImageUrl } from '../../api';
import {
  useLocationFormOptions,
  useLocationChangelog,
  useUploadLocationImage,
  useDeleteLocationImage,
} from '../../hooks/useLocations';
import type {
  Location,
  UpdateLocationInput,
  SiteOption,
  Transmitter,
  Sensor,
  Usergroup,
} from '../../types';
import { LOCATION_TAGS } from '../../types';

interface LocationFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  location: Location | null;
  sites?: SiteOption[];
  isLoadingSites?: boolean;
  onSubmit: (data: UpdateLocationInput) => Promise<void>;
  onDelete?: (locationId: string) => Promise<void>;
  isSubmitting: boolean;
  isDeleting?: boolean;
  canDelete?: boolean;
  isFullAdmin?: boolean;
  onOpenGpsPicker?: (lat?: number, lng?: number) => void;
}

/**
 * Get account name from location (V4 has accountName directly)
 */
function getAccountName(location: Location): string {
  if (location.accountName) return location.accountName;
  return '—';
}

/**
 * Get display label for a transmitter
 */
function getTransmitterLabel(t: Transmitter): string {
  return t.name || t.dev_id || t.customid || t._id;
}

/**
 * Get sub-label for a transmitter (shows battery info)
 */
function getTransmitterSubLabel(t: Transmitter): string {
  if (t.battery_voltage) {
    return `${t.dev_id || ''} • ${t.battery_voltage}V`;
  }
  return t.dev_id || '';
}

/**
 * Get display label for a sensor
 */
function getSensorLabel(s: Sensor): string {
  return s.name || s.search_name || s._id;
}

export function LocationFormModal({
  open,
  onOpenChange,
  location,
  sites = [],
  isLoadingSites = false,
  onSubmit,
  onDelete,
  isSubmitting,
  isDeleting,
  canDelete = false,
  isFullAdmin = false,
  onOpenGpsPicker,
}: LocationFormModalProps) {
  const { t } = useTranslation('locations');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  // Form state
  const [name, setName] = useState('');
  const [abbreviations, setAbbreviations] = useState('');
  const [roomNumber, setRoomNumber] = useState('');
  const [siteId, setSiteId] = useState<string | undefined>(undefined);
  const [phone, setPhone] = useState('');
  const [extension, setExtension] = useState('');
  const [fax, setFax] = useState('');
  const [faxExtension, setFaxExtension] = useState('');
  const [description, setDescription] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [disabled, setDisabled] = useState(false);
  const [editLatitude, setEditLatitude] = useState('');
  const [editLongitude, setEditLongitude] = useState('');
  // New form fields
  const [usergroup, setUsergroup] = useState<string | undefined>(undefined);
  const [selectedTransmitters, setSelectedTransmitters] = useState<string[]>([]);
  const [selectedSensors, setSelectedSensors] = useState<string[]>([]);

  // Get MongoDB IDs from location for API calls
  const mongoOrgId = typeof location?.organisation === 'string' ? location.organisation : location?.organisation?._id;
  const mongoLocationId = location?.mongoLocationId || location?._id;
  const locationId = location?.id || location?._id || location?.mongoLocationId;

  // Determine if we should fetch form options:
  // - Need mongoOrgId (always required)
  // - For existing locations, we need mongoLocationId to properly filter equipment
  // - If editing a V4-created location that hasn't synced to MongoDB yet (CDC lag),
  //   we may not have mongoLocationId - in that case, show a warning
  const hasCdcLag = Boolean(location?.id && !mongoLocationId);

  // Fetch form options (usergroups, transmitters, sensors)
  const { data: formOptions, isLoading: isLoadingFormOptions } = useLocationFormOptions(
    mongoOrgId,
    mongoLocationId
  );

  // Fetch changelog (only for existing locations)
  const { data: changelogData, isLoading: isLoadingChangelog } = useLocationChangelog(
    locationId
  );

  // Image mutations
  const uploadImage = useUploadLocationImage();
  const deleteImage = useDeleteLocationImage();

  // Reset form when location changes
  useEffect(() => {
    if (location) {
      setName(location.name || '');
      setAbbreviations(location.abbreviations?.join(', ') || '');
      setRoomNumber(location.roomNumber || location.room_number || '');
      setSiteId(location.siteId || undefined);
      setPhone(location.phone || '');
      setExtension(location.extension || '');
      setFax(location.fax || '');
      setFaxExtension(location.faxExtension || location.fax_extension || '');
      setDescription(location.description || '');
      setSelectedTags(location.tags || []);
      setDisabled(location.disabled || false);
      // Coordinates
      const lat = location.latitude ?? location.coordinates?.[0];
      const lng = location.longitude ?? location.coordinates?.[1];
      setEditLatitude(lat != null ? String(lat) : '');
      setEditLongitude(lng != null ? String(lng) : '');
      // New fields
      setUsergroup(
        location.mongoUsergroupId ||
          (typeof location.usergroup === 'string' ? location.usergroup : location.usergroup?._id) ||
          undefined
      );
      setSelectedTransmitters(location.transmitterIds || location.transmitters || []);
      setSelectedSensors(location.sensorIds || location.sensors || []);
      // Reset tab to details
      setActiveTab('details');
    } else {
      // Reset to empty
      setName('');
      setAbbreviations('');
      setRoomNumber('');
      setSiteId(undefined);
      setPhone('');
      setExtension('');
      setFax('');
      setFaxExtension('');
      setDescription('');
      setSelectedTags([]);
      setDisabled(false);
      setEditLatitude('');
      setEditLongitude('');
      setUsergroup(undefined);
      setSelectedTransmitters([]);
      setSelectedSensors([]);
      setActiveTab('details');
    }
  }, [location]);

  const handleTagToggle = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleImageUpload = async (file: File) => {
    if (!locationId) return;
    await uploadImage.mutateAsync({ locationId, file });
  };

  const handleImageDelete = async () => {
    if (!locationId) return;
    await deleteImage.mutateAsync(locationId);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const data: UpdateLocationInput = {
      name: name.trim(),
      abbreviations: abbreviations
        .split(',')
        .map((a) => a.trim())
        .filter(Boolean),
      roomNumber: roomNumber.trim() || undefined,
      siteId: siteId || null,
      phone: phone.trim() || undefined,
      extension: extension.trim() || undefined,
      fax: fax.trim() || undefined,
      faxExtension: faxExtension.trim() || undefined,
      description: description.trim() || undefined,
      tags: selectedTags,
      disabled,
      // Coordinates
      latitude: editLatitude ? parseFloat(editLatitude) : undefined,
      longitude: editLongitude ? parseFloat(editLongitude) : undefined,
      // New fields
      usergroup: usergroup || null,
      transmitters: selectedTransmitters,
      sensors: selectedSensors,
    };

    await onSubmit(data);
  };

  const handleDelete = async () => {
    if (location && onDelete) {
      // Use id (V4) or fallback to _id (V3 legacy) or mongoLocationId
      const locId = location.id || location._id || location.mongoLocationId;
      if (!locId) {
        toast.error('Cannot delete location: missing location ID');
        return;
      }
      await onDelete(locId);
      setShowDeleteDialog(false);
      onOpenChange(false);
    }
  };

  if (!location) return null;

  // Get image URL
  const imageUrl = getLocationImageUrl(mongoOrgId, location.mongoImageId || location.image);

  // Extract form options
  const usergroups: Usergroup[] = formOptions?.usergroups || [];
  const transmitters: Transmitter[] = formOptions?.transmitters || [];
  const sensors: Sensor[] = formOptions?.sensors || [];

  // Changelog data
  const changelog = changelogData?.changelog || [];

  // Is editing (not creating)
  const isEditing = Boolean(locationId);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('edit.title', 'Edit Location')}</DialogTitle>
            <DialogDescription>
              {getAccountName(location)}
              {location.siteName && ` • ${location.siteName}`}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="details" className="flex items-center gap-1">
                  <Building2 className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('tabs.details', 'Details')}</span>
                </TabsTrigger>
                <TabsTrigger value="contact" className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('tabs.contact', 'Contact')}</span>
                </TabsTrigger>
                <TabsTrigger value="equipment" className="flex items-center gap-1">
                  <Cpu className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('tabs.equipment', 'Equipment')}</span>
                </TabsTrigger>
                <TabsTrigger value="settings" className="flex items-center gap-1">
                  <Settings className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('tabs.settings', 'Settings')}</span>
                </TabsTrigger>
                {isEditing && (
                  <TabsTrigger value="history" className="flex items-center gap-1">
                    <History className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('tabs.history', 'History')}</span>
                  </TabsTrigger>
                )}
              </TabsList>

              {/* Details Tab */}
              <TabsContent value="details" className="space-y-4 mt-4">
                {/* Image Upload */}
                <div className="grid gap-2">
                  <Label>{t('form.image', 'Image')}</Label>
                  <LocationImageUpload
                    imageUrl={imageUrl}
                    onUpload={handleImageUpload}
                    onDelete={handleImageDelete}
                    isLoading={uploadImage.isPending || deleteImage.isPending}
                    disabled={isSubmitting}
                  />
                </div>

                <div className="grid gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="name">{t('edit.name', 'Name')} *</Label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="abbreviations">{t('edit.abbreviations', 'Abbreviations')}</Label>
                    <Input
                      id="abbreviations"
                      value={abbreviations}
                      onChange={(e) => setAbbreviations(e.target.value)}
                      placeholder={t('edit.abbreviationsPlaceholder', 'Comma-separated')}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="roomNumber">{t('edit.roomNumber', 'Room Number')}</Label>
                    <Input
                      id="roomNumber"
                      value={roomNumber}
                      onChange={(e) => setRoomNumber(e.target.value)}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="site">{t('edit.site', 'Site')}</Label>
                    <Select
                      value={siteId || 'none'}
                      onValueChange={(val) => setSiteId(val === 'none' ? undefined : val)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('edit.selectSite', 'Select a site')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t('edit.noSite', 'No site')}</SelectItem>
                        {!isLoadingSites &&
                          sites.map((site) => (
                            <SelectItem key={site.id} value={site.id}>
                              {site.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid gap-2">
                    <Label>{t('edit.description', 'Description')}</Label>
                    <RichTextEditor
                      value={description}
                      onChange={setDescription}
                      placeholder={t('edit.description', 'Description')}
                      disabled={isSubmitting}
                      minHeight="80px"
                    />
                  </div>
                </div>
              </TabsContent>

              {/* Contact Tab */}
              <TabsContent value="contact" className="space-y-4 mt-4">
                <div className="grid gap-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="phone">{t('edit.phone', 'Phone')}</Label>
                      <MaskedInput
                        mask="(000) 000-0000"
                        id="phone"
                        value={phone}
                        onAccept={(val) => setPhone(val)}
                        placeholder="(514) 555-1234"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="extension">{t('edit.extension', 'Ext.')}</Label>
                      <Input
                        id="extension"
                        value={extension}
                        onChange={(e) => setExtension(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="fax">{t('edit.fax', 'Fax')}</Label>
                      <MaskedInput
                        mask="(000) 000-0000"
                        id="fax"
                        value={fax}
                        onAccept={(val) => setFax(val)}
                        placeholder="(514) 555-1234"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="faxExtension">{t('edit.faxExtension', 'Ext.')}</Label>
                      <Input
                        id="faxExtension"
                        value={faxExtension}
                        onChange={(e) => setFaxExtension(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Geolocation */}
                  <div className="grid gap-2">
                    <Label>{t('edit.geolocation', 'Geolocation')}</Label>
                    {isFullAdmin ? (
                      <>
                        <div className="grid grid-cols-2 gap-4">
                          <Input
                            type="number"
                            step="any"
                            placeholder={t('form.latitude', 'Latitude')}
                            value={editLatitude}
                            onChange={(e) => setEditLatitude(e.target.value)}
                          />
                          <Input
                            type="number"
                            step="any"
                            placeholder={t('form.longitude', 'Longitude')}
                            value={editLongitude}
                            onChange={(e) => setEditLongitude(e.target.value)}
                          />
                        </div>
                        <div className="flex gap-2">
                          {onOpenGpsPicker && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => onOpenGpsPicker(
                                editLatitude ? parseFloat(editLatitude) : undefined,
                                editLongitude ? parseFloat(editLongitude) : undefined
                              )}
                            >
                              <MapPin className="h-4 w-4 mr-1" />
                              {t('form.openGpsPicker', 'Pick on Map')}
                            </Button>
                          )}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              if (!navigator.geolocation) return;
                              navigator.geolocation.getCurrentPosition(
                                (pos) => {
                                  setEditLatitude(pos.coords.latitude.toFixed(6));
                                  setEditLongitude(pos.coords.longitude.toFixed(6));
                                },
                                () => {}
                              );
                            }}
                          >
                            <MapPin className="h-4 w-4 mr-1" />
                            {t('form.useCurrentLocation', 'Current Location')}
                          </Button>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {editLatitude && editLongitude
                          ? `${editLatitude}, ${editLongitude}`
                          : '-'}
                      </p>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Equipment Tab */}
              <TabsContent value="equipment" className="space-y-6 mt-4">
                {hasCdcLag ? (
                  <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                    <Loader2 className="h-8 w-8 mb-2" />
                    <p className="text-center text-sm">
                      {t('form.cdcLagWarning', 'Equipment data is syncing. Please wait a moment and try again.')}
                    </p>
                  </div>
                ) : isLoadingFormOptions ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <>
                    {/* Transmitters */}
                    <div className="space-y-2">
                      <Label>{t('form.transmitters', 'Transmitters')}</Label>
                      <DualListBox
                        available={transmitters}
                        selected={selectedTransmitters}
                        onSelectionChange={setSelectedTransmitters}
                        getItemId={(item) => item._id}
                        getItemLabel={getTransmitterLabel}
                        getItemSubLabel={getTransmitterSubLabel}
                        availableLabel={t('form.availableTransmitters', 'Available')}
                        selectedLabel={t('form.selectedTransmitters', 'Assigned')}
                        searchPlaceholder={t('form.searchTransmitters', 'Search transmitters...')}
                        listHeight={200}
                        disabled={isSubmitting}
                      />
                    </div>

                    {/* Sensors */}
                    <div className="space-y-2">
                      <Label>{t('form.sensors', 'Sensors')}</Label>
                      <DualListBox
                        available={sensors}
                        selected={selectedSensors}
                        onSelectionChange={setSelectedSensors}
                        getItemId={(item) => item._id}
                        getItemLabel={getSensorLabel}
                        availableLabel={t('form.availableSensors', 'Available')}
                        selectedLabel={t('form.selectedSensors', 'Assigned')}
                        searchPlaceholder={t('form.searchSensors', 'Search sensors...')}
                        listHeight={200}
                        disabled={isSubmitting}
                      />
                    </div>
                  </>
                )}
              </TabsContent>

              {/* Settings Tab */}
              <TabsContent value="settings" className="space-y-4 mt-4">
                <div className="grid gap-4">
                  {/* User Group Dropdown */}
                  <div className="grid gap-2">
                    <Label htmlFor="usergroup">{t('form.usergroup', 'User Group')}</Label>
                    <Select
                      value={usergroup || 'none'}
                      onValueChange={(val) => setUsergroup(val === 'none' ? undefined : val)}
                      disabled={isLoadingFormOptions}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('form.selectUsergroup', 'Select a user group')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t('form.noUsergroup', 'No user group')}</SelectItem>
                        {usergroups.map((ug) => (
                          <SelectItem key={ug._id} value={ug._id}>
                            {ug.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>{t('edit.status', 'Status')}</Label>
                      <p className="text-sm text-muted-foreground">
                        {disabled
                          ? t('edit.statusInactive', 'Location is inactive')
                          : t('edit.statusActive', 'Location is active')}
                      </p>
                    </div>
                    <Switch
                      checked={!disabled}
                      onCheckedChange={(checked) => setDisabled(!checked)}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label>{t('edit.tags', 'Tags')}</Label>
                    <div className="flex flex-wrap gap-3">
                      {LOCATION_TAGS.map((tag) => (
                        <div key={tag} className="flex items-center space-x-2">
                          <Checkbox
                            id={`tag-${tag}`}
                            checked={selectedTags.includes(tag)}
                            onCheckedChange={() => handleTagToggle(tag)}
                          />
                          <label
                            htmlFor={`tag-${tag}`}
                            className="text-sm cursor-pointer"
                          >
                            {tag}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Read-only info */}
                  <div className="grid gap-2">
                    <Label>{t('edit.account', 'Account')}</Label>
                    <p className="text-sm text-muted-foreground">
                      {getAccountName(location)}
                      {location.accountNumber && ` (${location.accountNumber})`}
                    </p>
                  </div>

                  {(location.createdAt || location.created_at) && (
                    <div className="grid gap-2">
                      <Label>{t('edit.createdAt', 'Created At')}</Label>
                      <p className="text-sm text-muted-foreground">
                        {new Date(location.createdAt || location.created_at || '').toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* History Tab */}
              {isEditing && (
                <TabsContent value="history" className="mt-4">
                  <LocationChangelog
                    changelog={changelog}
                    isLoading={isLoadingChangelog}
                  />
                </TabsContent>
              )}
            </Tabs>

            <DialogFooter className="mt-6 flex justify-between">
              <div>
                {canDelete && onDelete && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => setShowDeleteDialog(true)}
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-1" />
                    )}
                    {t('edit.delete', 'Delete')}
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  {t('edit.cancel', 'Cancel')}
                </Button>
                <Button type="submit" disabled={isSubmitting || uploadImage.isPending || !name.trim()}>
                  {(isSubmitting || uploadImage.isPending) && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                  {uploadImage.isPending ? t('form.imageUploading', 'Uploading...') : t('edit.save', 'Save')}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('edit.deleteTitle', 'Delete location?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('edit.deleteDescription', 'This action cannot be undone. This will permanently delete the location "{{name}}".', { name: location?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('edit.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t('edit.delete', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
